import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import { Events, scrollSpy, animateScroll as scroll } from 'react-scroll';
import isEqual from 'lodash/isEqual';
import { MAX_MENU_WIDTH } from 'bento-ordering/base/constants/maxWidth';

import SearchBar from 'components/SearchBar';
import GridContainer from 'bento-ordering/base/ui_components/GridContainer';
import MenuSectionBarDropDown from 'components/MenuSectionBarDropDown';
import MenuSectionBarNavItem from 'components/MenuSectionBarNavItem';
import { slugify } from 'helpers/slugify';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { getSectionLabelsForMenuBarAndDropdown } from 'bento-ordering/menus/utils/menuSectionBar';
import {
  scrollToElement,
  AXIS,
  ALIGNMENT
} from 'bento-ordering/utils/scrollToElement';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';
import { OPACITY_20 } from 'constants/opacity';
import { getPageBackgroundColor } from 'styles/helpers/getThemeStyles';

const MIN_MENU_ITEMS_TO_SHOW_ONLINE_ORDERING_SEARCH = 20;

// These are mobile styling for the menu section bar in mobile.
// The menu section bar should be scrollable horizontally on overflow
// and the scrollbar should be hidden.

const StyledMenuSectionsContainer = styled(GridContainer)(
  ({ theme, isMobile }) => ({
    position: 'sticky',
    top: '64px',
    height: '65px',
    background: getPageBackgroundColor(theme),
    zIndex: theme.zIndex.appBar - 2,
    display: 'flex',
    paddingLeft: isMobile ? 0 : theme.spacing(2),
    paddingRight: isMobile ? 0 : theme.spacing(2)
  })
);

const StyledMenuSectionsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 0 8px',
  width: '100%',
  maxWidth: MAX_MENU_WIDTH,
  [theme.breakpoints.down('md')]: {
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  borderBottom: `1px solid ${calculateRgbaWithAlpha(
    theme.palette.sectionNav.link.main,
    OPACITY_20
  )}`
}));

const MENU_SECTIONS_BAR_ID = 'menu-sections-bar';

class MenuSectionsBarItems extends React.PureComponent {
  static propTypes = {
    menu: PropTypes.shape({
      id: PropTypes.number,
      sections: PropTypes.array
    }).isRequired,
    marginOffset: PropTypes.number.isRequired,

    numberOfMenuItems: PropTypes.number.isRequired,
    isSearchInputOpen: PropTypes.bool,
    handleOpenSearchInput: PropTypes.func,
    handleCloseSearchInput: PropTypes.func,
    searchTerm: PropTypes.string,
    setSearchTerm: PropTypes.func
  };

  static defaultProps = {
    isSearchInputOpen: false,
    handleOpenSearchInput: () => {},
    handleCloseSearchInput: () => {}
  };

  shouldOffsetNextScroll = true;
  shouldGraduallyNavigateNextScroll = true;
  isDesktop = !isMobileMedia(); // predeteremine if mobile regardless of resize
  isMobile = isMobileMedia();
  sectionItemWidths = [];
  sectionToBeActive = '';

  state = {
    activeMenuSectionName: '',
    activeMenuSectionsGroupIndex: 0,
    menuSectionItemsGroupedIntoViewableGroups: [this.props.menu.sections],
    moreDropdownMenuSectionItems: [],
    menuSectionOffset: 180
  };

  componentDidMount() {
    if (this.isDesktop) {
      this.setupMenuSections();
    }
    this.setupWindowResizeListener();
    this.setupScrollSpy();
    this.setupEndScrollListener();
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  componentDidUpdate(prevProps) {
    const { isSearchInputOpen, menu } = this.props;
    const searchInputWasOpen =
      !isSearchInputOpen && prevProps.isSearchInputOpen;

    if (
      menu !== prevProps.menu ||
      searchInputWasOpen ||
      (this.node &&
        this.node?.children.length !== this.sectionItemWidths.length)
    ) {
      this.setupMenuSections();
    }
  }

  setupWindowResizeListener() {
    window.addEventListener('resize', this.initializeResizeActions);
  }

  /**
   * Method that encapsulates all logic that needs to be executed on window resize
   */
  initializeResizeActions = () => {
    // only needed for desktop because mobile has scrolling functionality
    if (this.isDesktop) {
      this.determineMenuSections();
    }
  };

  /**
   * Renders all sections to be displayed first and determineMenuSection
   * will deteremine which sections should be hidden.
   * 1) Initially render all menu items on display section bar
   * 2) on call back, save all menu item width for future access
   * 3) deteremine if any items should be stored in the save
   */
  setupMenuSections() {
    this.storeSectionItemWidths();
    this.determineMenuSections();
  }

  /**
   * store the rendered width of all menu section items ahead of time,
   * so we do not have to access the dom elements on each new render
   */
  storeSectionItemWidths = () => {
    const sectionBarItems = Array.from(this.node?.children ?? {});

    this.sectionItemWidths = sectionBarItems.map(item => {
      return item.offsetWidth;
    });
  };

  determineMenuSections = () => {
    const { marginOffset, menu } = this.props;

    const {
      menuSectionItemsGroupedIntoViewableGroups,
      moreDropdownMenuSectionItems
    } = getSectionLabelsForMenuBarAndDropdown(
      marginOffset,
      menu.sections,
      this.sectionItemWidths
    );

    const doChangesExist =
      !isEqual(
        this.state.menuSectionItemsGroupedIntoViewableGroups,
        menuSectionItemsGroupedIntoViewableGroups
      ) ||
      !isEqual(
        this.state.moreDropdownMenuSectionItems,
        moreDropdownMenuSectionItems
      );

    if (doChangesExist) {
      this.setState({
        menuSectionItemsGroupedIntoViewableGroups,
        moreDropdownMenuSectionItems
      });
    }
  };

  setupScrollSpy() {
    scrollSpy.update();
  }

  /**
   * This listener is used to simulate gradual scrolling while navigating to a Menu
   * Section. Navigating directly is not desirable given the jarring user experience,
   * and gradually scrolling the whole way doesn't work because the lazy-loaded
   * components have dynamic heights, so this hybrid of navigating directly then
   * gradually scrolling only a small portion was created.
   * It does this in three ways:
   * 1) Navigates directly to the Menu Section header without any scroll duration to
   *    avoid the Menu Items being considered in view between where the user was and
   *    where they navigated to so that the Menu Section header y position doesn't
   *    change. <Link to>,  setup in render(), does this.
   * 2) When the initial scroll navigation is done, this listener's function is called.
   *    The offsetNextScroll() function is called to reposition the user n number of
   *    pixels above or below the Menu Section header, based on if they were navigated
   *    from a location above or below it. This is also done without any scroll duration.
   * 3) When the second scroll navigation is done, this listener is triggered again.
   *    The graduallyNavigateNextScroll() function is called to handle the gradual
   *    navigation to a little above the Menu Section header, either navigating down or
   *    up depending on if the user came from above or below. This gradual navigation
   *    is the main purpose of this listener because it couldn't be done by default on
   *    <Link> due to the lazy-loaded heights.
   * 4) When the third scroll navigation is done, this listener resets the needed
   *    offsetNextScroll and graduallyNavigateNextScroll for the next time navigation
   *    occurs.
   */
  setupEndScrollListener = () => {
    const self = this;
    let scrollingFromTopToBottom;
    let toIdx;

    /**
     * The variables are only setup on the first of three calls
     * when "to" is populated, with the values being used in the
     * subsequent calls. "To" is only populated on the first
     * call because that's the only time this listener is
     * triggered by onSetActive() of <Link>.
     * @param {string} to the Menu Section name to be activated
     */
    function setup(to) {
      if (to) {
        // sectionToBeActive is used temporarily store the section to be focused
        // during the lifecycle of the scrolling. This variable is used instead of
        // the dynamic value from the listener to prevent jitter from the render
        self.sectionToBeActive = to;
        toIdx = self.props.menu.sections.findIndex(
          section => slugify(section.name) === to
        );
        const fromIdx = self.props.menu.sections.findIndex(
          section => slugify(section.name) === self.state.activeMenuSectionName
        );
        scrollingFromTopToBottom = !(fromIdx >= 0) || toIdx >= fromIdx;
      }
    }

    function offsetNextScroll() {
      self.shouldOffsetNextScroll = false;
      const scrollOffset = scrollingFromTopToBottom
        ? -self.state.menuSectionOffset
        : self.state.menuSectionOffset;
      scroll.scrollMore(scrollOffset, { duration: 0 });
    }

    function graduallyNavigateNextScroll() {
      self.shouldGraduallyNavigateNextScroll = false;
      const scrollDistance = scrollingFromTopToBottom
        ? self.state.menuSectionOffset
        : -self.state.menuSectionOffset;
      scroll.scrollMore(scrollDistance, {
        smooth: 'easeOutQuad',
        duration: 340
      });
    }

    function setFocusToNavigatedElement() {
      if (self.isDesktop) {
        const element = document.getElementById(
          `${slugify(self.sectionToBeActive)}-header`
        );
        if (element !== null) {
          element.focus();
        }
      }
    }

    function reset() {
      self.shouldOffsetNextScroll = true;
      self.shouldGraduallyNavigateNextScroll = true;
      self.sectionToBeActive = '';
    }

    /**
     * This listener will be called three (3) times for
     * each click of a Menu Section in the Menu Section
     * navigation.
     */
    Events.scrollEvent.register('end', (to, element) => {
      setup(to);

      // for desktop, if menus are too small for scroll
      // we need to manually call set active so the sections
      // can be focused regardless of scrollability
      if (self.isDesktop) {
        self.handleSetActive(to);
      }

      self.resetGroupIndex();

      if (self.shouldOffsetNextScroll) {
        // Called first after initial user navigation
        offsetNextScroll();
      } else if (self.shouldGraduallyNavigateNextScroll) {
        // Called second after initial user navigation
        graduallyNavigateNextScroll();
      } else {
        // Called third after initial user navigation
        setFocusToNavigatedElement();
        reset();
      }
    });
  };

  removeListeners() {
    window.removeEventListener('resize', this.initializeResizeActions);
    Events.scrollEvent.remove('end');
  }

  /**
   * Used during tab navigation to reset which grouping of Menu Sections is
   * visible to the first one, which is what's visible when no tabbing happens
   */
  resetGroupIndex = () => {
    if (this.state.activeMenuSectionsGroupIndex !== 0) {
      this.setState({ activeMenuSectionsGroupIndex: 0 });
    }
  };

  handleSetActive(slugifiedMenuSectionName) {
    this.setState(
      {
        activeMenuSectionName:
          this.sectionToBeActive || slugifiedMenuSectionName
      },
      () => {
        if (this.isMobile) {
          const scrollToSection =
            this.sectionToBeActive || slugifiedMenuSectionName;
          this.autoscrollHorizontally(scrollToSection);
        } else {
          this.resetGroupIndex();
        }
      }
    );
  }

  // function adapted from this Stack Overflow response https://stackoverflow.com/a/51005649/4079905
  autoscrollHorizontally(slugifiedMenuSectionName) {
    const containerElement = document.getElementById(MENU_SECTIONS_BAR_ID);
    const targetElement = document.getElementById(
      `${slugifiedMenuSectionName}-nav-item`
    );
    const durationInMs = 150;

    scrollToElement(
      containerElement,
      targetElement,
      durationInMs,
      AXIS.X,
      ALIGNMENT.MIDDLE
    );
  }

  handleMenuSectionNavItemFocus(visibleGroupIndex) {
    if (this.state.activeMenuSectionsGroupIndex !== visibleGroupIndex) {
      this.setState({
        activeMenuSectionsGroupIndex: visibleGroupIndex
      });
    }
  }

  renderMainMenuSections() {
    const { menu } = this.props;
    const lastSectionItemIndex =
      menu.sections !== undefined ? menu.sections.length - 1 : [];
    const menuSectionsToRender = [];
    const self = this;

    this.state.menuSectionItemsGroupedIntoViewableGroups.forEach(
      (menuSectionItemsByGroup, groupIndex) => {
        Array.isArray(menuSectionItemsByGroup) &&
          menuSectionItemsByGroup.forEach(menuSection => {
            menuSectionsToRender.push(
              <MenuSectionBarNavItem
                key={`${menuSection.id}-bar`}
                menuSection={menuSection}
                index={menuSectionsToRender.length}
                lastSectionItemIndex={lastSectionItemIndex}
                handleSetActive={to => self.handleSetActive(to)}
                activeMenuSectionName={self.state.activeMenuSectionName}
                offset={-self.state.menuSectionOffset}
                isVisible={
                  self.state.activeMenuSectionsGroupIndex === groupIndex
                }
                onFocus={() => self.handleMenuSectionNavItemFocus(groupIndex)}
              />
            );
          });
      }
    );

    return menuSectionsToRender;
  }

  render() {
    const {
      numberOfMenuItems,
      isSearchInputOpen,
      handleOpenSearchInput,
      handleCloseSearchInput,
      searchTerm,
      setSearchTerm
    } = this.props;
    const isMobile = isMobileMedia();

    return (
      <StyledMenuSectionsContainer isMobile={isMobile}>
        {numberOfMenuItems >= MIN_MENU_ITEMS_TO_SHOW_ONLINE_ORDERING_SEARCH && (
          <SearchBar
            showSearchInput={isSearchInputOpen}
            openInputSearch={handleOpenSearchInput}
            closeInputSearch={handleCloseSearchInput}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        )}
        {!isSearchInputOpen && (
          <StyledMenuSectionsBox
            data-testid={MENU_SECTIONS_BAR_ID}
            id={MENU_SECTIONS_BAR_ID}
            ml="auto"
            mr="auto"
            ref={node => (this.node = node)}
          >
            {this.renderMainMenuSections()}
            <Box marginLeft="auto">
              {/* hiddenMenusectionSectionItems will not be populated on mobile */}
              {!isMobile &&
                this.state.moreDropdownMenuSectionItems.length > 0 && (
                  <MenuSectionBarDropDown
                    handleSetActive={to => this.handleSetActive(to)}
                    activeMenuSectionName={this.state.activeMenuSectionName}
                    menuSections={this.state.moreDropdownMenuSectionItems}
                    offset={-this.state.menuSectionOffset}
                  />
                )}
            </Box>
          </StyledMenuSectionsBox>
        )}
      </StyledMenuSectionsContainer>
    );
  }
}

export default MenuSectionsBarItems;
